import { memo, useState, ReactNode, useCallback, Dispatch, SetStateAction } from 'react'

import { Box, Skeleton, Typography, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { DynamicBreadcrumbs } from '@shared/components/Header'
import { ManagedTitle, ManagedTitleConditionalProps } from '@shared/components/ManagedTitle'
import { Kbd } from '@shared/components/MaterialUIEnhancements'
import { currentOS, OS } from '@shared/utils/common'
import { HotkeyItem } from '@shared/utils/hooks'

const Wrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: theme.spacing(0.5, 0.5, 0, 0),
}))

const TitleWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  marginBottom: theme.spacing(0.8),
  height: '100%',
}))

const Progress = styled(Skeleton)(({ theme }) => ({
  padding: theme.spacing(0.2, 0),
  ...theme.typography['heading-xl'],
  width: '100%',
  maxWidth: 480,
}))

const RightSideWrapper = styled(Box)(({ theme }) => ({
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  columnGap: theme.spacing(2),
  height: '100%',
}))

type HeaderProps = {
  title: string
  appendix?: ReactNode
  Actions?: ReactNode
  isLoading?: boolean
  isSaving?: boolean
}
const HeaderRaw = ({
  title,
  appendix,
  Actions,
  disabled,
  isLoading,
  isSaving,
  onSave,
}: HeaderProps & ManagedTitleConditionalProps) => {
  const { t } = useTranslation()

  const [isEditing, setIsEditing] = useState(false)

  const handleHotKeyItems = useCallback(
    (onStateChange: Dispatch<SetStateAction<boolean>>) => [
      [
        'shift+mod+e',
        () => {
          if (!disabled) {
            onStateChange(true)
          }
        },
      ] satisfies HotkeyItem,
    ],
    [disabled],
  )

  return (
    <Wrapper>
      <DynamicBreadcrumbs />

      <TitleWrapper>
        {isLoading ? (
          <Progress />
        ) : (
          <ManagedTitle
            title={title}
            placeholder={t('noNameSet')}
            {...(!disabled
              ? {
                  onSave,
                  isLoading: isSaving,
                  clickAwayBehaviour: 'save',
                  hotKeyItems: handleHotKeyItems,
                  onManagedStateChange: (state) => setIsEditing(state),
                }
              : {
                  disabled,
                })}
          />
        )}

        {!isEditing ? (
          <>
            {!disabled && (
              <Typography sx={{ ml: 0.5, flexShrink: 0 }} variant="text-sm">
                <Kbd>{`shift-${currentOS === OS.MAC_OS ? '⌘' : 'Ctrl'}-E`}</Kbd>
              </Typography>
            )}

            {appendix}

            <RightSideWrapper>{Actions}</RightSideWrapper>
          </>
        ) : (
          <Kbd>⏎</Kbd>
        )}
      </TitleWrapper>
    </Wrapper>
  )
}

export const Header = memo(HeaderRaw)
